import React, { useCallback } from 'react';
import * as coreBi from '#packages/coreBi';
import type {
  ResolvedStyleBreakpointRelation,
  StyleDataItem,
  StyleRef,
} from '@wix/document-services-types';
import { userPreferences } from '#packages/stateManagement';
import {
  Composites,
  ToggleSwitch,
  Divider,
  Slider,
  TextLabel,
} from '@wix/wix-base-ui';
import { hoc } from '#packages/util';
import type { StateMapperArgs, Dispatch } from 'types/redux';
import type { SendBiFunction } from 'types/bi';

interface Props {
  paramKey: string;
  styleDataItem: StyleDataItem;
  tabsUiMode?: 'labels' | 'tabs';
  forceUpdateParentStyle: FunctionFixMe;
  sendBI: SendBiFunction;
  sendSiteSegmentBi: (biValues: {
    field_value: 'on' | 'off';
    mouse_state: 'scroll' | 'regular';
    tab_name: string;
    field_name: string;
  }) => void;
}

interface MappedProps {
  userPrefKeys: Record<string, string>;
  valueInPrefs: string;
  valueInData: string;
  enabledInPrefs: string;
  opacityValueInData: string;
  setToUserPrefs: (key: string, value: string) => void;
}

const MAX_BLUR_VAL = 50;
const MIN_BLUR_VAL = 1;
const DEFAULT_FILTER_VAL = 'blur(20px)';
const DEFAULT_OPACITY_VAL = 0.3;
const OPACITY_THRESHOLD_FOR_BLUR = 0.5;
const BG_ALPHA_KEY = 'alpha-bg';
const APPLY_BACKDROP_FILTER = 'apply_BackdropFilter';
const BACKDROP_FILTER_VALUE = 'backdropFilter_value';
export const SLIDER_LABEL = 'CustomDesign_BackdropFilter_Blur_Value';
export const TOGGLE_LABEL = 'CustomDesign_BackdropFilter_Toggle_Label';

const filterType = 'blur';
const filterValueMatcher = /\w+\((\d+)(\w*)\)/;
const getFilterValueAndUnits = (
  filterValue: string,
): { value: number; units: string } => {
  if (filterValue === 'none') {
    return { value: 0, units: 'px' };
  }
  const [, value, units = 'px'] = filterValue.match(filterValueMatcher) || [];
  return { value: parseInt(value, 10), units };
};

export const AdvancedStyleBackdropFilterControl = (
  props: MappedProps & Props,
) => {
  const {
    valueInPrefs,
    valueInData,
    enabledInPrefs,
    opacityValueInData,
    setToUserPrefs,
    userPrefKeys,
    paramKey,
    styleDataItem,
    forceUpdateParentStyle,
    sendSiteSegmentBi,
    sendBI,
    tabsUiMode,
  } = props;

  const style = (styleDataItem as StyleRef).style;

  const sendBi = useCallback(
    (biValues: Record<string, string>) => {
      sendBI(coreBi.events.designPanel.BOX_DESIGN_SETTINGS, {
        ...biValues,
        panel_name: 'advancedStyle',
      });
    },
    [sendBI],
  );

  const { value, units } = getFilterValueAndUnits(
    valueInPrefs || valueInData || DEFAULT_FILTER_VAL,
  );
  const opacity = parseInt(opacityValueInData || '1', 10);
  const enabled =
    enabledInPrefs === 'true' || (valueInData && valueInData !== 'none');

  const changeFillOpacity = useCallback(
    (opacityOverride: number) => {
      style.properties[BG_ALPHA_KEY] = `${opacityOverride}`; // set container opacity value in data
      forceUpdateParentStyle(styleDataItem, BG_ALPHA_KEY, `${opacityOverride}`);
    },
    [forceUpdateParentStyle, style.properties, styleDataItem],
  );

  const onBackdropFilterEnabledChange = useCallback(
    (isEnabled: boolean) => {
      if (isEnabled) {
        const filter = `${filterType}(${value}${units})`;
        style.properties[paramKey] = filter; // set blur value in data
        forceUpdateParentStyle(styleDataItem, paramKey, filter);

        const shouldOverrideOpacity =
          typeof valueInPrefs === 'undefined' &&
          opacity > OPACITY_THRESHOLD_FOR_BLUR;

        if (shouldOverrideOpacity) {
          changeFillOpacity(DEFAULT_OPACITY_VAL);
        }
      } else {
        setToUserPrefs(userPrefKeys.value, valueInData || 'none');
        style.properties[paramKey] = 'none';
        forceUpdateParentStyle(styleDataItem, paramKey, 'none');
      }

      setToUserPrefs(userPrefKeys.enabled, isEnabled ? 'true' : 'false');

      const valueForBI = isEnabled ? 'on' : 'off';

      sendSiteSegmentBi?.({
        field_value: valueForBI,
        mouse_state: 'regular',
        tab_name: 'fill',
        field_name: APPLY_BACKDROP_FILTER,
      });

      sendBi({
        field_name: APPLY_BACKDROP_FILTER,
        field_value: valueForBI,
      });
    },
    [
      value,
      units,
      valueInPrefs,
      valueInData,
      styleDataItem,
      forceUpdateParentStyle,
      changeFillOpacity,
      setToUserPrefs,
      userPrefKeys,
      sendSiteSegmentBi,
      sendBi,
      opacity,
      paramKey,
      style.properties,
    ],
  );

  const onBackdropFilterValueChange = useCallback(
    (newVal: number) => {
      const param = paramKey;
      const filter = `${filterType}(${newVal}${units})`;
      style.properties[param] = filter;
      forceUpdateParentStyle(styleDataItem, param, filter);
      setToUserPrefs(userPrefKeys.value, filter);
    },
    [
      styleDataItem,
      setToUserPrefs,
      forceUpdateParentStyle,
      paramKey,
      style.properties,
      units,
      userPrefKeys.value,
    ],
  );

  const onBackdropFilterSlideEnd = (newVal: number) => {
    sendBi({
      field_name: BACKDROP_FILTER_VALUE,
      field_value: `${newVal}${units}`,
    });
  };

  return (
    <>
      <Composites.ToggleSwitch>
        <ToggleSwitch
          value={enabled}
          onChange={onBackdropFilterEnabledChange}
          shouldTranslate={true}
          size={'MEDIUM'}
          label={TOGGLE_LABEL}
          labelType={tabsUiMode === 'tabs' ? 'T02' : 'T12'}
        />
      </Composites.ToggleSwitch>
      {enabled && (
        <>
          <Divider long={false} />
          <Composites.SliderLabeled>
            <TextLabel shouldTranslate={true} value={SLIDER_LABEL} />
            <Slider
              value={value}
              unit={units}
              min={MIN_BLUR_VAL}
              max={MAX_BLUR_VAL}
              onChange={onBackdropFilterValueChange}
              onSlideEnd={onBackdropFilterSlideEnd}
              key="backdropFilterControl"
            />
          </Composites.SliderLabeled>
        </>
      )}
    </>
  );
};

AdvancedStyleBackdropFilterControl.displayName =
  'AdvancedStyleBackdropFilterControl';

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setToUserPrefs: (key: string, value: string) => {
    dispatch(userPreferences.actions.setSessionUserPreferences(key, value));
  },
});

const mapStateToProps = ({ state }: StateMapperArgs, props: Props) => {
  const prefsId = `${props.styleDataItem.id}-${
    (props.styleDataItem as ResolvedStyleBreakpointRelation).breakpoint
  }`;

  const userPrefKeys = {
    enabled: `backdropBlurEnabled-${prefsId}`,
    value: `backdropBlurValue-${prefsId}`,
  };

  const valueInData: string = (props.styleDataItem as StyleRef).style
    .properties[props.paramKey];

  const valueInPrefs: string =
    userPreferences.selectors.getSessionUserPreferences(userPrefKeys.value)(
      state,
    );

  const enabledInPrefs = userPreferences.selectors.getSessionUserPreferences(
    userPrefKeys.enabled,
  )(state);

  const opacityValueInData: string = (props.styleDataItem as StyleRef).style
    .properties[BG_ALPHA_KEY];

  return {
    userPrefKeys,
    valueInPrefs,
    valueInData,
    enabledInPrefs,
    opacityValueInData,
  };
};

const connectedAdvancedStyleBackdropFilterControl = hoc.connect(
  hoc.STORES.EDITOR_API,
  mapStateToProps,
  mapDispatchToProps,
)(AdvancedStyleBackdropFilterControl);

export default connectedAdvancedStyleBackdropFilterControl;
